import { useCallback, useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { useAuth } from '../context/AuthContext';
import { fetchSoftphoneToken, sendSoftphoneHeartbeat, unregisterSoftphone } from './softphoneApi';

const HEARTBEAT_MS = 30000;

export default function useSoftphone({ enabled = true } = {}) {
  const { token } = useAuth();
  const [status, setStatus] = useState('offline');
  const [caller, setCaller] = useState('');
  const [muted, setMuted] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const startedAtRef = useRef(null);

  const record = useCallback((outcome) => {
    setHistory((entries) => [
      {
        id: `${Date.now()}-${entries.length}`,
        from: callRef.current?.parameters?.From || '',
        outcome,
        at: new Date(),
        duration: startedAtRef.current ? Math.round((Date.now() - startedAtRef.current) / 1000) : 0,
      },
      ...entries,
    ].slice(0, 50));
  }, []);

  const clearCall = useCallback(() => {
    callRef.current = null;
    startedAtRef.current = null;
    setCaller('');
    setMuted(false);
    setSeconds(0);
    setStatus(deviceRef.current ? 'ready' : 'offline');
  }, []);

  useEffect(() => {
    if (!token || !enabled) {
      setStatus('offline');
      return undefined;
    }

    let cancelled = false;
    let device = null;

    const start = async () => {
      try {
        const { token: accessToken } = await fetchSoftphoneToken();
        if (cancelled) return;

        device = new Device(accessToken, { codecPreferences: ['opus', 'pcmu'], logLevel: 'error' });
        deviceRef.current = device;

        device.on('registered', () => {
          if (!cancelled) {
            setError('');
            setStatus('ready');
          }
        });

        device.on('error', (deviceError) => {
          if (cancelled) return;
          setError(deviceError?.message || 'Softphone error');
          setStatus('error');
        });

        device.on('tokenWillExpire', async () => {
          try {
            const refreshed = await fetchSoftphoneToken();
            device.updateToken(refreshed.token);
          } catch (refreshError) {
            setError('Session expired — reload the page');
          }
        });

        device.on('incoming', (call) => {
          if (cancelled) return;
          callRef.current = call;
          setCaller(call.parameters?.From || '');
          setStatus('ringing');
          setError('');

          call.on('accept', () => {
            startedAtRef.current = Date.now();
            setSeconds(0);
            setStatus('active');
          });
          call.on('cancel', () => {
            record('missed');
            clearCall();
          });
          call.on('reject', () => {
            record('declined');
            clearCall();
          });
          call.on('disconnect', () => {
            record(startedAtRef.current ? 'answered' : 'missed');
            clearCall();
          });
          call.on('error', (callError) => {
            setError(callError?.message || 'Call error');
            record('failed');
            clearCall();
          });
        });

        await device.register();
      } catch (startError) {
        if (cancelled) return;
        setError(startError?.response?.data?.error || startError?.message || 'Could not start the softphone');
        setStatus('error');
      }
    };

    start();

    const heartbeat = setInterval(() => {
      sendSoftphoneHeartbeat().catch(() => {});
    }, HEARTBEAT_MS);

    const handleUnload = () => unregisterSoftphone();
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      cancelled = true;
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      unregisterSoftphone();
      if (device) {
        try {
          device.destroy();
        } catch (destroyError) {
          setError('');
        }
      }
      deviceRef.current = null;
      callRef.current = null;
    };
  }, [token, enabled, clearCall, record]);

  useEffect(() => {
    if (status !== 'active') return undefined;
    const timer = setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [status]);

  const answer = useCallback(async () => {
    if (!callRef.current) return;
    try {
      await callRef.current.accept();
    } catch (acceptError) {
      setError(acceptError?.message || 'Could not answer — check microphone permission');
    }
  }, []);

  const decline = useCallback(() => {
    if (!callRef.current) return;
    callRef.current.reject();
    record('declined');
    clearCall();
  }, [clearCall, record]);

  const hangUp = useCallback(() => {
    if (!callRef.current) return;
    callRef.current.disconnect();
    record('answered');
    clearCall();
  }, [clearCall, record]);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    setMuted((current) => {
      callRef.current.mute(!current);
      return !current;
    });
  }, []);

  return { status, caller, muted, seconds, error, history, answer, decline, hangUp, toggleMute };
}
